import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Stack,
  Alert,
  Snackbar,
  Avatar,
  Drawer,
  IconButton as MuiIconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import {
  LocalShippingOutlined,
  PaymentOutlined,
  CheckCircleOutline,
  CancelOutlined,
  PendingOutlined,
  StarOutline,
  Star,
  ReceiptOutlined,
  DateRangeOutlined,
  LocationOnOutlined,
  PhoneOutlined,
  EmailOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Close as CloseIcon,
  ArrowBack,
  InfoOutlined,
  ExpandMore,
  Receipt,
  Payments,
  LocalOffer,
  Storefront,
  PersonRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { OrderDetails, TrackOrder, OrderItemDetails } from "../../services/api/profileService";

const GOLD = "#f3de9f";
const NAVY = "#001f54";

const statusColors = {
  "Payment Successful": {
    color: "#4caf50",
    bg: "#e8f5e9",
    icon: <CheckCircleOutline />,
  },
  Processing: { color: "#ff9800", bg: "#fff3e0", icon: <PendingOutlined /> },
  Shipped: { color: "#2196f3", bg: "#e3f2fd", icon: <LocalShippingOutlined /> },
  Delivered: { color: "#4caf50", bg: "#e8f5e9", icon: <CheckCircleOutline /> },
  cancelled: { color: "#f44336", bg: "#ffebee", icon: <CancelOutlined /> },
  pending: { color: "#ff9800", bg: "#fff3e0", icon: <PendingOutlined /> },
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Not available';
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Optimized OrderCard with reduced height and less padding
const OrderCard = ({ order, onTrack, onOrderDetails, onRate }) => {
  const [expanded, setExpanded] = useState(false);
  const statusInfo = statusColors[order.status] || statusColors["pending"];

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 2,
        transition: "all 0.2s ease",
        bgcolor: '#ffff',
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {/* Order Header - Compact */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, fontSize: "0.95rem" }}>
              Order #{order.orderNumber}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
              <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "#666" }}>
                <DateRangeOutlined sx={{ fontSize: 12 }} />
                {formatDate(order.createdAt)}
              </Typography>
              <Chip
                label={order.status}
                size="small"
                icon={statusInfo.icon}
                sx={{
                  bgcolor: statusInfo.bg,
                  color: statusInfo.color,
                  fontWeight: 600,
                  height: 20,
                  fontSize: "0.7rem",
                  "& .MuiChip-icon": { fontSize: 12 },
                }}
              />
            </Box>
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, fontSize: "0.95rem" }}>
            ₹{parseFloat(order.totalAmount).toFixed(2)}
          </Typography>
        </Box>

        {/* Order Items - Compact */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {order.items.slice(0, 3).map((item, idx) => (
            <Box key={idx} sx={{ position: "relative" }}>
              <Avatar
                src={item.image}
                variant="rounded"
                sx={{ width: 45, height: 45, borderRadius: 1.5 }}
              >
                {!item.image && "📦"}
              </Avatar>
              {idx === 2 && order.items.length > 3 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -3,
                    right: -3,
                    bgcolor: NAVY,
                    color: GOLD,
                    borderRadius: "50%",
                    width: 16,
                    height: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 8,
                    fontWeight: "bold",
                  }}
                >
                  +{order.items.length - 3}
                </Box>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            {order.items.length} item{order.items.length > 1 ? "s" : ""}
          </Typography>
        </Box>

        {/* Action Buttons - Compact */}
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<InfoOutlined sx={{ fontSize: 16 }} />}
            onClick={() => onOrderDetails(order)}
            sx={{
              borderColor: NAVY,
              color: NAVY,
              fontSize: "0.7rem",
              py: 0.3,
              px: 1,
              "&:hover": { borderColor: GOLD, bgcolor: GOLD },
            }}
          >
            Details
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LocalShippingOutlined sx={{ fontSize: 16 }} />}
            onClick={() => onTrack(order)}
            sx={{
              borderColor: NAVY,
              color: NAVY,
              fontSize: "0.7rem",
              py: 0.3,
              px: 1,
              "&:hover": { borderColor: GOLD, bgcolor: GOLD },
            }}
          >
            Track
          </Button>
          {order.status === "Delivered" && (
            <Button
              size="small"
              variant="contained"
              startIcon={<StarOutline sx={{ fontSize: 16 }} />}
              onClick={() => onRate(order)}
              sx={{
                bgcolor: NAVY,
                fontSize: "0.7rem",
                py: 0.3,
                px: 1,
                "&:hover": { bgcolor: "#002b78" },
              }}
            >
              Rate
            </Button>
          )}
          <IconButton onClick={() => setExpanded(!expanded)} size="small" sx={{ p: 0.5 }}>
            {expanded ? <KeyboardArrowUp sx={{ fontSize: 18 }} /> : <KeyboardArrowDown sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>

        {/* Expanded Details - Keep as is but compact */}
        {expanded && (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid rgba(0,31,84,0.1)` }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, color: NAVY, display: 'block' }}>
              Order Details
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <strong>Address:</strong> {order.postalCode}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  <strong>Status:</strong> {order.deliveryStatus}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <strong>Products:</strong> {order.totalProducts}
                </Typography>
                {order.actualDeliveryTime && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Delivered:</strong> {formatDate(order.actualDeliveryTime)}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ fontWeight: 600, mt: 1, mb: 0.5, color: NAVY, display: 'block' }}>
              Items
            </Typography>
            {order.items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  gap: 1,
                  py: 0.5,
                  borderBottom: `1px solid rgba(0,31,84,0.05)`,
                }}
              >
                <Avatar src={item.image} variant="rounded" sx={{ width: 35, height: 35 }}>
                  {!item.image && "📦"}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {item.productName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {item.quantity}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Order Details Dialog Component
const OrderDetailsDialog = ({ open, onClose, order }) => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState({});

  useEffect(() => {
    if (open && order) {
      fetchOrderDetails();
    }
  }, [open, order]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await OrderItemDetails(order.orderNumber, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(response.data?.data);
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVendorExpand = (vendorId) => {
    setExpandedVendors(prev => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  if (!orderData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        bgcolor: NAVY, 
        color: GOLD, 
        p: 2.5,
        borderBottom: `1px solid rgba(243,222,159,0.2)`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
              Order Details
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
              #{orderData.orderNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: GOLD }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: NAVY }} />
          </Box>
        ) : (
          <Box>
            {/* Order Summary Banner */}
            <Box sx={{ 
              bgcolor: 'white', 
              p: 3, 
              borderBottom: '1px solid #e0e0e0',
              background: `linear-gradient(135deg, ${NAVY} 0%, #002b78 100%)`,
              color: 'white'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Order Status</Typography>
                  <Chip 
                    label={orderData.status}
                    size="small"
                    sx={{ 
                      mt: 0.5,
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Order Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {formatDateTime(orderData.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: GOLD }}>
                    ₹{parseFloat(orderData.totalAmount).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Customer Information */}
            <Box sx={{ p: 3, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonRounded sx={{ fontSize: 20 }} />
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {orderData.customerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {orderData.customerEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {orderData.customerPhone}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Delivery Address */}
            <Box sx={{ p: 3, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnOutlined sx={{ fontSize: 20 }} />
                Delivery Address
              </Typography>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {orderData.addressLine1}
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                {orderData.city}, {orderData.state} - {orderData.postalCode}
              </Typography>
              {orderData.deliveryInstructions && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: NAVY }}>
                  📝 {orderData.deliveryInstructions}
                </Typography>
              )}
            </Box>

            {/* Vendor Orders Section */}
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Storefront sx={{ fontSize: 20 }} />
                Vendor Orders
              </Typography>
              
              {orderData.vendorOrders?.map((vendor, idx) => (
                <Accordion 
                  key={vendor.id}
                  expanded={expandedVendors[vendor.id]}
                  onChange={() => toggleVendorExpand(vendor.id)}
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    '&:before': { display: 'none' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>
                          {vendor.vendorName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Order #{vendor.vendorOrderNumber}
                        </Typography>
                      </Box>
                      <Chip
                        label={vendor.status}
                        size="small"
                        sx={{
                          bgcolor: vendor.status === 'delivered' ? '#e8f5e9' : '#fff3e0',
                          color: vendor.status === 'delivered' ? '#4caf50' : '#ff9800',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Vendor Contact */}
                    {vendor.phoneNumber && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneOutlined sx={{ fontSize: 16 }} />
                          Vendor Contact: {vendor.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Items Table */}
                    <TableContainer component={Paper} sx={{ mb: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vendor.items?.map((item, itemIdx) => (
                            <TableRow key={itemIdx}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {item.productName}
                                </Typography>
                                {item.hsnCode && (
                                  <Typography variant="caption" color="text.secondary">
                                    HSN: {item.hsnCode}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">{item.quantity}</TableCell>
                              <TableCell align="right">₹{parseFloat(item.unitPrice).toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                ₹{parseFloat(item.totalPrice).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Vendor Totals */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: NAVY }}>
                        Subtotal: ₹{parseFloat(vendor.itemsSubtotal).toFixed(2)}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* Charges Breakdown */}
            {orderData.charges && orderData.charges.length > 0 && (
              <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOffer sx={{ fontSize: 20 }} />
                  Charges Breakdown
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Table size="small">
                    <TableBody>
                      {orderData.charges.map((charge, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{charge.chargeName}</TableCell>
                          <TableCell align="right">
                            ₹{parseFloat(charge.calculatedAmount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total Amount</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: NAVY }}>
                          ₹{parseFloat(orderData.totalAmount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Payment Information */}
            {orderData.payments && orderData.payments.length > 0 && (
              <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payments sx={{ fontSize: 20 }} />
                  Payment Information
                </Typography>
                {orderData.payments.map((payment, idx) => (
                  <Box key={idx} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Method</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {payment.paymentMethod}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                          ₹{parseFloat(payment.amount).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Paid At</Typography>
                        <Typography variant="body2">
                          {formatDateTime(payment.paidAt)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}

            {/* Invoices */}
            {orderData.invoices && orderData.invoices.length > 0 && (
              <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt sx={{ fontSize: 20 }} />
                  Invoices
                </Typography>
                {orderData.invoices.map((invoice, idx) => (
                  <Chip
                    key={idx}
                    label={`${invoice.invoiceType.toUpperCase()}: ${invoice.invoiceNumber}`}
                    sx={{ mr: 1, mb: 1, bgcolor: NAVY, color: GOLD }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Enhanced TrackOrderDialog with beautiful UI
const TrackOrderDialog = ({ open, onClose, order }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState({});

  useEffect(() => {
    if (open && order) {
      fetchTrackingDetails();
    }
  }, [open, order]);

  const fetchTrackingDetails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await TrackOrder(order.orderNumber, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrackingData(response.data);
    } catch (error) {
      console.error("Error fetching tracking details:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVendorExpand = (vendorId) => {
    setExpandedVendors(prev => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  const getOrderProgress = (status) => {
    const steps = {
      'pending': 0,
      'received': 1,
      'preparing': 2,
      'ready': 3,
      'picked_up': 4,
      'delivered': 5
    };
    return steps[status] || 0;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'delivered': return <CheckCircleOutline sx={{ fontSize: 20, color: '#4caf50' }} />;
      case 'picked_up': return <LocalShippingOutlined sx={{ fontSize: 20, color: '#2196f3' }} />;
      case 'ready': return <CheckCircleOutline sx={{ fontSize: 20, color: '#ff9800' }} />;
      case 'preparing': return <PendingOutlined sx={{ fontSize: 20, color: '#ff9800' }} />;
      default: return <PendingOutlined sx={{ fontSize: 20, color: '#ff9800' }} />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Order Received',
      'received': 'Order Confirmed',
      'preparing': 'Being Prepared',
      'ready': 'Ready for Pickup',
      'picked_up': 'Out for Delivery',
      'delivered': 'Delivered'
    };
    return statusMap[status] || status;
  };

  const getDeliveryStatusColor = (status) => {
    const colors = {
      'planned': '#ff9800',
      'assigned': '#2196f3',
      'picked_up': '#9c27b0',
      'in_transit': '#ff9800',
      'delivered': '#4caf50',
      'failed': '#f44336'
    };
    return colors[status] || '#757575';
  };

  const getDeliveryStatusText = (status) => {
    const texts = {
      'planned': 'Delivery Planned',
      'assigned': 'Partner Assigned',
      'picked_up': 'Picked Up',
      'in_transit': 'In Transit',
      'delivered': 'Delivered',
      'failed': 'Delivery Failed'
    };
    return texts[status] || status;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: NAVY, 
        color: GOLD, 
        p: 2.5,
        borderBottom: `1px solid rgba(243,222,159,0.2)`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
              Track Order
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
              #{order?.orderNumber}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: GOLD }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: NAVY }} />
          </Box>
        ) : trackingData?.data ? (
          <Box>
            <Box sx={{ 
              bgcolor: 'white', 
              p: 2.5, 
              borderBottom: '1px solid #e0e0e0',
              background: `linear-gradient(135deg, ${NAVY} 0%, #002b78 100%)`,
              color: 'white'
            }}>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Order placed on {formatDateTime(trackingData.data.createdAt)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Customer: {trackingData.data.customerName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Chip 
                  label={trackingData.data.overallStatus.toUpperCase()}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total: ₹{parseFloat(trackingData.data.totalAmount).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {trackingData.data.promisedDeliveryTime && (
              <Box sx={{ p: 2.5, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: NAVY, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateRangeOutlined sx={{ fontSize: 18 }} />
                   Delivery Promise : {formatDateTime(trackingData.data.promisedDeliveryTime)}
                </Typography>
              </Box>
            )}

            <Box sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingOutlined />
                Order Status by Vendor
              </Typography>
              
              {trackingData.data.vendorOrders?.map((vendor, idx) => (
                <Card key={vendor.vendorOrderId} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: expandedVendors[vendor.vendorOrderId] ? '#f5f5f5' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#fafafa' }
                    }}
                    onClick={() => toggleVendorExpand(vendor.vendorOrderId)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY }}>
                          {vendor.vendorName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {vendor.address}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip
                          icon={getStatusIcon(vendor.status)}
                          label={getStatusText(vendor.status)}
                          size="small"
                          sx={{
                            bgcolor: vendor.status === 'delivered' ? '#e8f5e9' : '#fff3e0',
                            color: vendor.status === 'delivered' ? '#4caf50' : '#ff9800',
                            fontWeight: 600
                          }}
                        />
                        <IconButton size="small">
                          {expandedVendors[vendor.vendorOrderId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  {expandedVendors[vendor.vendorOrderId] && (
                    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: NAVY, mb: 1, display: 'block' }}>
                          Order Progress
                        </Typography>
                        <Stepper activeStep={getOrderProgress(vendor.status)} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>
                          <Step><StepLabel>Received</StepLabel></Step>
                          <Step><StepLabel>Preparing</StepLabel></Step>
                          <Step><StepLabel>Ready</StepLabel></Step>
                          <Step><StepLabel>Picked Up</StepLabel></Step>
                          <Step><StepLabel>Delivered</StepLabel></Step>
                        </Stepper>
                      </Box>
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ReceiptOutlined sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
            <Typography color="text.secondary">No tracking information available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RatingDialog = ({ open, onClose, order, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/orders/rate/${order.orderNumber}`,
        { rating, review, productId: order.items[0].masterProductUuid },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSubmit(order.orderNumber, rating, review);
      onClose();
    } catch (error) {
      console.error("Error submitting rating:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: GOLD }}>Rate Your Order</DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" gutterBottom>
          How was your experience with Order #{order?.orderNumber}?
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <Rating
            value={rating}
            onChange={(event, newValue) => setRating(newValue)}
            size="large"
            icon={<Star sx={{ fontSize: 40 }} />}
            emptyIcon={<StarOutline sx={{ fontSize: 40 }} />}
          />
        </Box>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Write your review (optional)"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={rating === 0 || submitting} sx={{ bgcolor: NAVY }}>
          {submitting ? <CircularProgress size={24} /> : "Submit Rating"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Drawer Component with Infinite Scroll
const MyOrdersDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [trackOrder, setTrackOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [rateOrder, setRateOrder] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  
  const observerRef = useRef();
  const lastOrderRef = useRef();
  const limit = 10;

  const fetchOrders = useCallback(async (pageNum = 1, append = false) => {
    if (!user?.uuid) return;
    if (loading) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await OrderDetails({
        customerUUID: user?.uuid,
        page: pageNum,
        limit: limit,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.success) {
        const newOrders = response.data.data;
        const total = response.data.total;
        setTotalOrders(total);
        if (append) {
          setOrders(prev => [...prev, ...newOrders]);
        } else {
          setOrders(newOrders);
        }
        setHasMore(orders.length + newOrders.length < total);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setSnackbar({ open: true, message: "Failed to load orders", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [user?.uuid, loading, orders.length]);

  useEffect(() => {
    if (open) {
      setOrders([]);
      setPage(1);
      setHasMore(true);
      fetchOrders(1, false);
    }
  }, [open]);

  useEffect(() => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchOrders(page + 1, true);
        }
      },
      { threshold: 0.1 }
    );
    if (lastOrderRef.current) observerRef.current.observe(lastOrderRef.current);
    return () => { if (observerRef.current) observerRef.current.disconnect(); };
  }, [loading, hasMore, page, orders.length, fetchOrders]);

  const handleTrackOrder = (order) => setTrackOrder(order);
  const handleOrderDetails = (order) => setOrderDetails(order);
  const handleRateOrder = (order) => setRateOrder(order);
  const handleRatingSubmit = (orderNumber, rating, review) => {
    setSnackbar({ open: true, message: `Thank you for rating Order #${orderNumber}!`, severity: "success" });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 550, md: 650 },
            bgcolor: GOLD,
            borderRadius: { xs: 0, sm: "20px 0 0 20px" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ bgcolor: NAVY, px: 2.5, py: 2, position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MuiIconButton onClick={onClose} size="small" sx={{ color: NAVY, bgcolor: "#e8c97a", "&:hover": { bgcolor: "#d4b86a" } }}>
                <ArrowBack fontSize="small" />
              </MuiIconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, fontFamily: "'Playfair Display', serif" }}>
                My Orders
              </Typography>
            </Box>
            <MuiIconButton onClick={onClose} size="small" sx={{ color: GOLD, "&:hover": { bgcolor: "rgba(243,222,159,0.1)" } }}>
              <CloseIcon fontSize="small" />
            </MuiIconButton>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, color: GOLD, opacity: 0.9 }}>
            {totalOrders} order{totalOrders !== 1 ? "s" : ""} found
          </Typography>
        </Box>

        <Box sx={{ bgcolor: NAVY, height: 12 }}>
          <Box sx={{ height: "100%", bgcolor: GOLD, clipPath: "ellipse(100% 97% at 50% 0%)" }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1.5 }}>
          {loading && orders.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress sx={{ color: NAVY }} />
            </Box>
          ) : orders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, bgcolor: "#fff" }}>
              <ReceiptOutlined sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Typography variant="h6" gutterBottom>No Orders Yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You haven't placed any orders yet.
              </Typography>
              <Button variant="contained" onClick={() => { onClose(); navigate("/"); }} sx={{ bgcolor: NAVY }}>
                Start Shopping
              </Button>
            </Paper>
          ) : (
            <>
              {orders.map((order, index) => (
                <div key={order.orderNumber} ref={index === orders.length - 1 ? lastOrderRef : null}>
                  <OrderCard
                    order={order}
                    onTrack={handleTrackOrder}
                    onOrderDetails={handleOrderDetails}
                    onRate={handleRateOrder}
                  />
                </div>
              ))}
              {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={30} sx={{ color: NAVY }} /></Box>}
              {!hasMore && orders.length > 0 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Divider sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">No more orders to load</Typography></Divider>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <TrackOrderDialog open={!!trackOrder} onClose={() => setTrackOrder(null)} order={trackOrder} />
      <OrderDetailsDialog open={!!orderDetails} onClose={() => setOrderDetails(null)} order={orderDetails} />
      <RatingDialog open={!!rateOrder} onClose={() => setRateOrder(null)} order={rateOrder} onSubmit={handleRatingSubmit} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default MyOrdersDrawer;